import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

interface Notification {
  id: string
  user_id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  read: boolean
  action_url?: string | null
  action_label?: string | null
  created_at: string
}

interface UseNotificationsOptions {
  pageSize?: number
  enablePagination?: boolean
}

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { pageSize = 20, enablePagination = false } = options

  const {
    data: notificationsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useQuery({
    queryKey: ['notifications', user?.id, pageSize],
    queryFn: async () => {
      if (!user) return { notifications: [], hasMore: false, totalCount: 0 }

      try {
        let query = supabase
          .from('user_notifications')
          .select('*', { count: 'exact' })
          .eq('user_id', user.id)
          .eq('admin_only', false)
          .order('created_at', { ascending: false })

        if (!enablePagination) {
          query = query.limit(pageSize)
        }

        const { data, error, count } = await query

        if (error) {
          // If the table doesn't exist or there's another error, return empty array
          return { notifications: [], hasMore: false, totalCount: 0 }
        }

        return {
          notifications: data as Notification[],
          hasMore: enablePagination && (data?.length || 0) >= pageSize,
          totalCount: count || 0
        }
      } catch (error) {
        return { notifications: [], hasMore: false, totalCount: 0 }
      }
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000 // Refetch every minute
  })

  const notifications = notificationsData?.notifications || []
  const totalCount = notificationsData?.totalCount || 0

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length

  // Mutation to mark a notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user?.id) // Security: ensure user can only update their own notifications

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Notificação marcada como lida')
    },
    onError: (error) => {
      toast.error('Erro ao marcar notificação como lida')
    }
  })

  // Mutation to mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated')

      // Use the PostgreSQL function for better performance
      const { error } = await supabase.rpc('mark_all_notifications_read', {
        p_user_id: user.id
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Todas as notificações foram marcadas como lidas')
    },
    onError: (error) => {
      toast.error('Erro ao marcar todas as notificações como lidas')
    }
  })

  // Mutation to delete a notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('user_notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user?.id) // Security: ensure user can only delete their own notifications

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Notificação removida')
    },
    onError: (error) => {
      toast.error('Erro ao remover notificação')
    }
  })

  // Helper functions for mutations
  const markAsRead = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId)
  }

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  const deleteNotification = (notificationId: string) => {
    deleteNotificationMutation.mutate(notificationId)
  }

  // Atualização por polling (refetchInterval acima). Realtime não é usado:
  // user_notifications não está na publication supabase_realtime — decisão de
  // 2026-10-17 (incidente de memória/swap): manter só polling.

  return {
    notifications,
    unreadCount,
    totalCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    // Pagination support
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}